import type { GriffelStyle } from "@fluentui/react-components";
import {
  createCustomFocusIndicatorStyle,
  shorthands,
  makeStyles,
} from "@fluentui/react-components";
import { getHighContrastNoAdjustStyle } from "@fluentui/style-utilities";
import { customTokens } from "@1js/acui-theme";
import { controlStylingConstants } from "../controlStylingConstants";

export const HighContrastSelector = "@media (forced-colors: active)";

export const commonHighContrastBackgroundHoverFocus: GriffelStyle = {
  backgroundColor: "Highlight",
};

export const commonMediumContrastBackgroundHoverFocus: GriffelStyle = {
  backgroundColor: "HighlightText",
};

export const commonHighContrastColorHoverFocus: GriffelStyle = {
  color: "HighlightText",
};

export const commonMediumContrastColorHoverFocus: GriffelStyle = {
  color: "Highlight",
};

export const commonHighContrastHoverFocus: GriffelStyle = {
  ...commonHighContrastColorHoverFocus,
  ...commonHighContrastBackgroundHoverFocus,
  outlineColor: "ButtonText",
  ...(getHighContrastNoAdjustStyle() as GriffelStyle),
};

export const commonHighContrastOutlineHoverFocus: GriffelStyle = {
  outlineColor: "ButtonText",
  outlineStyle: "dashed",
  outlineWidth: "2px",
};

export const commonHighContrastButtonRestStyles: GriffelStyle = {
  backgroundColor: "ButtonFace",
  outlineColor: "inherit",
  outlineStyle: "inherit",
  outlineWidth: "inherit",
};

export const commonHighContrastColorButtonRest: GriffelStyle = {
  color: "ButtonText",
};

export const commonHighContrastDisabledStyles: GriffelStyle = {
  ...shorthands.borderColor("GrayText"),
  opacity: "1",
};

export const commonHighContrastColorDisabled: GriffelStyle = {
  color: "GrayText",
};

export const commonHighContrastFocusedDisabledStyles: GriffelStyle = {
  backgroundColor: "Highlight",
  ...shorthands.borderColor("GrayText"),
  opacity: "1",
};

export const commonHighContrastColorFocusedDisabled: GriffelStyle = {
  color: "GrayText",
};

export const commonHighContrastOutlineFocusedDisabled: GriffelStyle = {
  outlineColor: "GrayText",
  outlineWidth: "2px",
  outlineStyle: "dotted",
};

export const commonHighContrastHoveredDisabledStyles: GriffelStyle = {
  backgroundColor: "transparent",
};

export const commonHighContrastOutlineToggledOn: GriffelStyle = {
  ...shorthands.borderColor("Highlight"),
  outlineColor: "ButtonText",
  outlineWidth: "2px",
  outlineStyle: "solid",
};

export const commonHighContrastBorder: GriffelStyle = {
  ...shorthands.borderColor("Window"),
  outlineStyle: "none",
};

export const commonHighContrastRestStyles: GriffelStyle = {
  ...shorthands.borderWidth("1px"),
  ...shorthands.borderStyle("solid"),
};

export const commonHighContrastHoverBorderStyles: GriffelStyle = {
  ...shorthands.borderColor("Highlight"),
};

export const minimumTargetSizedStyles: GriffelStyle = {
  width: `${controlStylingConstants.minTargetSize}px`,
  minWidth: `${controlStylingConstants.minTargetSize}px`,
  minHeight: `${controlStylingConstants.minTargetSize}px`,
  height: `${controlStylingConstants.minTargetSize}px`,
};

// TODO 5340136: Replace with values from @1js/acui-button when babel-make-styles is fixed and we get to the variation of buttons in the peripheral controls area.
const borderThicknessFocused: string = "2";

export const useCommonFocusStyles = makeStyles({
  base: {
    [":focus::after"]: {
      content: '""',
      color: customTokens.legacy_black,
      position: "absolute",
      left: "-1px",
      top: "-1px",
      bottom: "-1px",
      right: "-1px",
      ...shorthands.borderWidth(`${borderThicknessFocused}px`),
      ...shorthands.borderStyle("solid"),
      pointerEvents: "none",
    },
    // Override Fluent's focus styles
    ...createCustomFocusIndicatorStyle({
      ...shorthands.borderRadius("0px"),
      boxShadow: "none",
    }),
  },
});

export const hiddenContentStyle: GriffelStyle = {
  position: "absolute",
  width: "1px",
  height: "1px",
  ...shorthands.margin("-1px"),
  ...shorthands.padding("0px"),
  ...shorthands.border("0px"),
  ...shorthands.overflow("hidden"),
};

export const useCommonDisabledStyles = makeStyles({
  base: {
    opacity: ".4",
    [`${HighContrastSelector}`]: {
      ...commonHighContrastDisabledStyles,
    },
  },
});

export const commonHighContrastCanvasSubStyles: GriffelStyle = {
  background: "Canvas",
  color: "CanvasText",
};

export const useCommonHighContrastCanvasStyles = makeStyles({
  base: {
    [`${HighContrastSelector}`]: {
      ...commonHighContrastCanvasSubStyles,
      ...(getHighContrastNoAdjustStyle() as GriffelStyle),
    },
  },
});

export const useCursorDisabledStyles: GriffelStyle = {
  cursor: "not-allowed !important",
};

export const useSelectedIconHighContrastStyles: GriffelStyle = {
  "& path.OfficeIconColors_HighContrast": {
    fill: `HighlightText !important`,
  },
};

export const useHoveredIconHighContrastStyles: GriffelStyle = {
  "& path.OfficeIconColors_HighContrast": {
    fill: `Highlight !important`,
  },
};
